import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  FormHelperText,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  type SelectChangeEvent,
} from "@mui/material";
import { API_TYPES } from "@src/appV2/api/constants";
import {
  GET_FACILITY_PROFILES_API_URL,
  type GetChildWorkplacesResponse,
  useGetAllChildWorkplaces,
} from "@src/appV2/FacilityProfile/api/useGetAllChildWorkplaces";
import { useUpdateWorkplacePlacementSettings } from "@src/appV2/FacilityProfile/api/useUpdateWorkplacePlacementSettings";
import { I18N_NAMESPACES } from "@src/appV2/i18n/constants";
import { useTu } from "@src/appV2/i18n/useTu";
import { useToast } from "@src/appV2/lib";
import { useDebounce } from "@src/appV2/lib/utils";
import { fetchPlacementsApiUrl } from "@src/appV2/Placements/api/useFetchPaginatedPlacements";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";

import { Text } from "../../../components/Text";

interface ChildFacilitiesSelectProps {
  workplaceId: string;
}

export function ChildFacilitiesSelect({ workplaceId }: ChildFacilitiesSelectProps) {
  const { tu } = useTu(I18N_NAMESPACES.settings);
  const { showSuccessToast, showErrorToast } = useToast();
  const [value, setValue] = useState<string[]>([]);
  const [dirty, setDirty] = useState(false);
  const queryClient = useQueryClient();

  const { mutateAsync: updateFacilityPlacementSettings, isLoading: isUpdateLoading } =
    useUpdateWorkplacePlacementSettings({
      onSuccess: ({ data }) => {
        setDirty(false);
        showSuccessToast(tu("Updated settings"));
        queryClient.setQueryData(
          [GET_FACILITY_PROFILES_API_URL, { parentFacilityId: workplaceId }],
          (previous: GetChildWorkplacesResponse | undefined) => {
            if (!previous) {
              return undefined;
            }

            return {
              ...previous,
              data: previous.data.map((facility) => ({
                ...facility,
                attributes: {
                  ...facility.attributes,
                  placementSettings: {
                    ...facility.attributes.placementSettings,
                    managedByParentWorkplace: data.find((dataItem) => dataItem.id === facility.id)
                      ?.attributes.managedByParentWorkplace,
                  },
                },
              })),
            };
          }
        );

        void queryClient.invalidateQueries({
          queryKey: [GET_FACILITY_PROFILES_API_URL],
        });
        void queryClient.invalidateQueries({
          queryKey: [fetchPlacementsApiUrl(workplaceId)],
        });
      },
      onError: () => {
        showErrorToast(tu("Error updating facility profile"));
      },
      onSettled: () => {
        void queryClient.resetQueries({
          queryKey: [fetchPlacementsApiUrl(workplaceId)],
        });
      },
    });

  const { data: childWorkplaces, isSuccess: isGetChildWorkplacesSuccess } =
    useGetAllChildWorkplaces({
      parentFacilityId: workplaceId,
    });

  useEffect(() => {
    setValue(
      childWorkplaces?.data
        .filter((facility) => facility.attributes.placementSettings?.managedByParentWorkplace)
        .map((facility) => facility.id) ?? []
    );
  }, [childWorkplaces]);

  const onUpdateValue = useCallback(
    async (selectedIds: string[]) => {
      if (!dirty) {
        return;
      }

      if (!childWorkplaces) {
        return;
      }

      const data = childWorkplaces.data.map((childWorkplace) => ({
        type: API_TYPES.workplace as typeof API_TYPES.workplace,
        id: childWorkplace.id,
        attributes: {
          managedByParentWorkplace: selectedIds.includes(childWorkplace.id),
        },
      }));

      await updateFacilityPlacementSettings({ data });
    },
    [dirty, childWorkplaces, updateFacilityPlacementSettings]
  );

  useDebounce({
    value,
    onUpdateValue,
  });

  const options =
    childWorkplaces?.data.map((facility) => ({
      value: facility.id,
      label: facility.attributes.name ?? "",
    })) ?? [];

  if (!isGetChildWorkplacesSuccess || childWorkplaces.data.length === 0) {
    return null;
  }

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    setDirty(true);
    setValue(event.target.value as string[]);
  };

  return (
    <FormControl sx={(theme) => ({ paddingTop: theme.spacing(2) })}>
      <Text semibold sx={(theme) => ({ paddingBottom: theme.spacing(1) })} variant="body2">
        {tu("Manage jobs for my child facilities")}
      </Text>
      <Select
        multiple
        disabled={isUpdateLoading}
        input={<OutlinedInput aria-label={tu("Select facilities")} size="small" />}
        label={tu("Select facilities")}
        MenuProps={{
          PaperProps: {
            sx: {
              maxHeight: "40vh",
              width: 300,
              overflowY: "auto",
            },
          },
        }}
        renderValue={(selected) =>
          selected.length === 0 ? (
            <Text variant="body2">{tu("None")}</Text>
          ) : (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {selected.map((selectedId) => {
                const option = options.find((item) => item.value === selectedId);
                return option ? <Chip key={selectedId} label={option.label} size="small" /> : null;
              })}
            </Box>
          )
        }
        sx={{ width: 300 }}
        value={value}
        onChange={handleChange}
      >
        {options.map((option) => (
          <MenuItem key={option.value} disabled={isUpdateLoading} value={option.value}>
            <Checkbox checked={value.includes(option.value)} />
            <ListItemText
              primary={option.label}
              primaryTypographyProps={{ sx: { whiteSpace: "normal", wordBreak: "break-word" } }}
            />
          </MenuItem>
        ))}
      </Select>
      <FormHelperText sx={{ marginLeft: 0 }}>
        {tu("Select the facilities that you want to manage jobs for.")}
      </FormHelperText>
    </FormControl>
  );
}
